import { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_BRAND = {
    platform_name: 'Voxoora',
    tagline: 'Immersive Audio Experiences',
    logo_url: null,
    favicon_url: null,
    primary_color: '#D4AF37',
    secondary_color: '#0A0D14',
    accent_color: '#1A1F2E',
    text_color: '#FFFFFF',
    font_family: 'Outfit',
    footer_text: null,
    support_email: null,
    support_url: null
};

const BrandContext = createContext(DEFAULT_BRAND);

export function BrandProvider({ children }) {
    const [brand, setBrand] = useState(DEFAULT_BRAND);

    const resolveFileUrl = (url) => {
        if (!url) return url;
        const idx = url.indexOf('/api/files/');
        if (idx !== -1) return `${API}${url.substring(idx)}`;
        return url;
    };

    useEffect(() => {
        axios.get(`${API}/api/brand`)
            .then(res => {
                const merged = { ...DEFAULT_BRAND, ...res.data };
                merged.logo_url = resolveFileUrl(merged.logo_url);
                merged.favicon_url = resolveFileUrl(merged.favicon_url);
                setBrand(merged);
                applyCSSVars(merged);
            })
            .catch(() => {
                applyCSSVars(DEFAULT_BRAND);
            });
    }, []);

    const hexToRgb = (hex) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `${r} ${g} ${b}`;
    };

    const hexToHsl = (hex) => {
        let r = parseInt(hex.slice(1, 3), 16) / 255;
        let g = parseInt(hex.slice(3, 5), 16) / 255;
        let b = parseInt(hex.slice(5, 7), 16) / 255;
        const max = Math.max(r, g, b), min = Math.min(r, g, b);
        let h = 0, s = 0, l = (max + min) / 2;
        if (max !== min) {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
            else if (max === g) h = ((b - r) / d + 2) / 6;
            else h = ((r - g) / d + 4) / 6;
        }
        return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
    };

    const applyCSSVars = (b) => {
        const root = document.documentElement;
        if (b.primary_color) {
            root.style.setProperty('--brand-gold', b.primary_color);
            root.style.setProperty('--gold-rgb', hexToRgb(b.primary_color));
            root.style.setProperty('--primary', hexToHsl(b.primary_color));
            root.style.setProperty('--ring', hexToHsl(b.primary_color));
        }
        if (b.secondary_color) {
            root.style.setProperty('--brand-bg', b.secondary_color);
            root.style.setProperty('--background', hexToHsl(b.secondary_color));
        }
        if (b.accent_color) {
            root.style.setProperty('--brand-accent', b.accent_color);
            root.style.setProperty('--accent', hexToHsl(b.accent_color));
        }
        if (b.platform_name) document.title = b.platform_name;
    };

    const refreshBrand = async () => {
        try {
            const res = await axios.get(`${API}/api/brand`);
            const merged = { ...DEFAULT_BRAND, ...res.data };
            merged.logo_url = resolveFileUrl(merged.logo_url);
            merged.favicon_url = resolveFileUrl(merged.favicon_url);
            setBrand(merged);
            applyCSSVars(merged);
        } catch (e) {
            console.error('Failed to refresh brand:', e);
        }
    };

    return (
        <BrandContext.Provider value={{ ...brand, refreshBrand }}>
            {children}
        </BrandContext.Provider>
    );
}

export const useBrand = () => useContext(BrandContext);
